import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Fingerprint, User, Briefcase, Building2, Lock, EyeOff, Zap } from "lucide-react";
import { matrixStrings as ms } from "../../lib/matrixStrings";

const inputCls =
  "w-full rounded-lg border border-teal-400/25 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 backdrop-blur transition-colors duration-200 focus:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-400/20";

const TYPE_ICONS = { osoba: User, podnikatel: Briefcase, firma: Building2 };
const TRUST_ICONS = [Lock, EyeOff, Zap];

export const MatrixEntry = ({ lang, onComplete }) => {
  const [clientType, setClientType] = useState("osoba");
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logIdx, setLogIdx] = useState(0);

  useEffect(() => {
    if (!syncing) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / 1300, 1);
      setProgress(Math.round(p * 100));
      setLogIdx(Math.min(2, Math.floor(p * 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(() => onComplete({ clientType }), 400);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [syncing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (syncing) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center text-center" data-testid="matrix-syncing">
        <div className="relative h-44 w-44">
          <motion.div
            className="absolute inset-0 rounded-full border border-teal-400/40"
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            style={{ borderTopColor: "#2DD4BF", borderRightColor: "transparent" }}
          />
          <motion.div
            className="absolute inset-4 rounded-full border border-teal-400/25"
            animate={{ rotate: -360 }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            style={{ borderBottomColor: "#5EEAD4", borderLeftColor: "transparent" }}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Fingerprint size={34} className="text-teal-300" />
            <span className="font-mono mt-3 text-2xl font-bold text-white" data-testid="sync-progress">{progress}%</span>
          </div>
        </div>
        <p className="font-mono mt-8 text-sm tracking-widest text-teal-300" data-testid="sync-log">
          {ms.entry.syncing[lang][logIdx]}
          <span className="mg-blink">_</span>
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto max-w-xl"
      data-testid="matrix-entry"
    >
      <p className="text-center font-mono text-[11px] font-semibold tracking-[0.3em] text-teal-400">{ms.entry.kicker[lang]}</p>
      <h1 className="font-heading mg-glow-text mt-4 text-center text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
        Zirsen
        <span className="block text-2xl text-teal-300 sm:text-3xl">{ms.entry.title[lang]}</span>
      </h1>
      <p className="mt-5 text-center text-sm leading-relaxed text-slate-400 sm:text-base">{ms.entry.sub[lang]}</p>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="entry-trust-badges">
        {ms.entry.trust.map((t, i) => {
          const Icon = TRUST_ICONS[i];
          return (
            <span key={i} className="flex items-center gap-1.5 text-xs font-medium text-teal-300/90">
              <Icon size={14} className="text-teal-400" />
              {t[lang]}
            </span>
          );
        })}
      </div>

      <div className="mg-glass mt-8 rounded-2xl p-6 sm:p-8">
        <label className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-widest text-teal-300/80">
          {ms.entry.typeLabel[lang]}
        </label>
        <div className="grid grid-cols-3 gap-2" data-testid="matrix-type-picker">
          {Object.entries(ms.entry.types).map(([key, label]) => {
            const Icon = TYPE_ICONS[key];
            const active = clientType === key;
            return (
              <button
                type="button"
                key={key}
                onClick={() => setClientType(key)}
                data-testid={`matrix-type-${key}`}
                className={`flex flex-col items-center gap-2 rounded-xl border px-2 py-4 transition-all duration-200 ${
                  active
                    ? "border-teal-300 bg-teal-400/15 text-teal-200 shadow-[0_0_20px_rgba(45,212,191,0.15)]"
                    : "border-teal-400/20 bg-white/5 text-slate-300 hover:border-teal-300/50"
                }`}
              >
                <Icon size={20} className={active ? "text-teal-300" : "text-slate-500"} />
                <span className="text-xs font-semibold leading-tight">{label[lang]}</span>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setSyncing(true)}
          data-testid="matrix-sync-button"
          className="mt-8 w-full rounded-full bg-teal-500 py-3.5 text-sm font-bold uppercase tracking-widest text-[#052E2B] transition-all duration-200 hover:bg-teal-300 hover:shadow-[0_0_30px_rgba(45,212,191,0.4)]"
        >
          {ms.entry.syncBtn[lang]}
        </button>
        <p className="mt-4 text-center text-[11px] leading-relaxed text-slate-500" data-testid="entry-ai-note">{ms.entry.aiNote[lang]}</p>
      </div>
    </motion.div>
  );
};
